import { useEffect, useRef } from 'react';
import { useMap } from 'rwl-ui';
import { useGlobal } from './useGlobal';
import { useMapContext } from './MapProvider';

export const useMapEvents = () => {
    const { map } = useMap();
    const { config } = useGlobal();
    const {
        serviceAreas,
        neighborhood,
        openMarkerId,
        setOpenMarkerId,
        serviceAreaHoverStore,
        setHoveredServiceAreaPlaceId,
        markerActionsRef,
    } = useMapContext();
    const openMarkerIdRef = useRef(openMarkerId);

    useEffect(() => {
        openMarkerIdRef.current = openMarkerId;
    }, [openMarkerId]);

    // Effect 1: Set up event listeners (stable — no hover/open deps)
    useEffect(() => {
        if (!map) return;

        const zipCodeLayer = map.getFeatureLayer(
            google.maps.FeatureType.POSTAL_CODE
        );

        let overFeatureThisFrame = false;
        let rafId: number | undefined;

        const handleFeatureMouseMove = (
            event: google.maps.FeatureMouseEvent
        ) => {
            overFeatureThisFrame = true;
            // @ts-ignore - event.features[0].placeId is not recognized
            const placeId = event.features?.[0]?.placeId;
            const area = serviceAreas?.find(
                (a) => a.placeInfo?.place_id === placeId
            );
            setHoveredServiceAreaPlaceId(area ? placeId : undefined);
        };

        const handleMapMouseMove = () => {
            overFeatureThisFrame = false;
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                if (!overFeatureThisFrame) {
                    setHoveredServiceAreaPlaceId(undefined);
                }
            });
        };

        const handleClick = (event: google.maps.FeatureMouseEvent) => {
            // @ts-ignore - event.features[0].placeId is not recognized
            const placeId = event.features?.[0]?.placeId;
            const area = serviceAreas?.find(
                (a) => a.placeInfo?.place_id === placeId
            );
            if (area) {
                // Use the marker's own open callback — same path as a marker click
                const openMarker = markerActionsRef.current.get(placeId);
                if (openMarker) {
                    openMarker();
                    return;
                }
            }
            // Click was on a polygon but not on a service area marker — close any open popover
            setOpenMarkerId(undefined);
        };

        const mouseMoveListener = zipCodeLayer.addListener(
            'mousemove',
            handleFeatureMouseMove
        );
        const clickListener = zipCodeLayer.addListener('click', handleClick);
        const mapMoveListener = map.addListener(
            'mousemove',
            handleMapMouseMove
        );

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            google.maps.event.removeListener(mouseMoveListener);
            google.maps.event.removeListener(clickListener);
            google.maps.event.removeListener(mapMoveListener);
        };
    }, [map, serviceAreas]);

    // Effect 2: Apply polygon style + subscribe to hover changes
    useEffect(() => {
        if (!map) return;

        const zipCodeLayer = map.getFeatureLayer(
            google.maps.FeatureType.POSTAL_CODE
        );

        const applyStyle = () => {
            const hoveredPlaceId = serviceAreaHoverStore.current.placeId;
            const currentOpenMarkerId = openMarkerIdRef.current;

            zipCodeLayer.style = (option) => {
                // @ts-ignore - option.feature.placeId is not recognized
                const placeId = option.feature.placeId;
                const area = serviceAreas?.find(
                    (a) => a.placeInfo?.place_id === placeId
                );
                if (
                    area &&
                    (!neighborhood ||
                        neighborhood.placeInfo?.place_id === placeId)
                ) {
                    const fillColor = config?.account.mapSettings['color']
                        ? config.account.mapSettings['color']
                        : '#3862d7';
                    const isActive =
                        hoveredPlaceId === placeId ||
                        currentOpenMarkerId === placeId ||
                        neighborhood?.placeInfo?.place_id === placeId;
                    return {
                        fillColor,
                        fillOpacity: isActive ? 0.25 : 0.1,
                        strokeWeight: neighborhood ? 2 : 1,
                        strokeColor: '#9E9E9E',
                    };
                }

                return;
            };
        };

        // Apply style once on mount so polygons are visible immediately,
        // then subscribe so hover changes re-apply with updated highlight state.
        applyStyle();

        const store = serviceAreaHoverStore.current;
        store.listeners.add(applyStyle);

        return () => {
            store.listeners.delete(applyStyle);
        };
    }, [map, serviceAreas, neighborhood, config, openMarkerId]);
};
